import { Component } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import {
  Client,
  DownloadFraudAlarmDataIn,
  FraudAlarmDataIn,
  FraudAlarmDataOut,
  PageInfo,
  PermissionsEnum,
  SortingType,
} from '../../../api/api-reference';
import { DateRangeInput } from '../../core/models/date-range-input';
import { SortComponent } from '../../core/components/sort/sort.component';
import { DatepickerComponent } from '../../core/components/datepicker/datepicker.component';
import { SearchComponent } from '../../core/components/search/search.component';
import { PaginationComponent } from '../../core/components/pagination/pagination.component';
import { CommonModule, DatePipe } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../../../services/auth.service';
import { SecondsToText } from '../../../pipes/seconds-to-text';
import { ClipboardService } from '../../../services/clipboard.service';
import { FileService } from "../../../services/file.service";
import { processDateRange } from '../../../helper/date-utils';
import { generateFileNameAppendix } from '../../../helper/download-filename';


@Component({
  selector: 'app-fraudulent-meters',
  standalone: true,
  imports: [
    TranslateModule,
    SortComponent,
    DatepickerComponent,
    SearchComponent,
    PaginationComponent,
    DatePipe,
    SecondsToText,
    CommonModule
  ],
  templateUrl: './fraudulent-meters.component.html',
  styleUrl: './fraudulent-meters.component.scss'
})
export class FraudulentMetersComponent {
  public pageSize: number = 10;
  public pageNumber: number = 1;
  public currentPage: number = 1;
  public numberOfMeters: number = 0;
  public lastPageSize: number = 1;
  public PermissionsEnum = PermissionsEnum;
  data: FraudAlarmDataOut[] | undefined = [];


  readonly dayInMs = 24 * 60 * 60 * 1000;
  isDatepickerOpen = false;

  requestData = new FraudAlarmDataIn({
    pageInfo: new PageInfo({ pageSize: this.pageSize, page: this.pageNumber }),
    search: undefined,
    startDate: undefined,
    endDate: undefined,
    sorting: SortingType.Descending,
  });

  constructor(
    private client: Client,
    private toastr: ToastrService,
    public authService: AuthService,
    private fileService: FileService,
    public clipboardService: ClipboardService
  ) {
    this.reloadTable(1);
  }

  reloadTable(page?: number) {
    this.pageNumber = page || 0;
    if (page) this.requestData!.pageInfo!.page = this.pageNumber;

    this.client.getFraudulentMeters(this.requestData).subscribe({
      next: (response) => {
        this.data = response.data;
        this.numberOfMeters = response.count || 0;
        this.lastPageSize = Math.ceil(this.numberOfMeters / this.pageSize);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  searchChange(searchText: string) {
    this.requestData.search = searchText;
    this.reloadTable(1);
  }

  pageChange(value: number) {
    this.currentPage = value;
    this.reloadTable(this.currentPage);
  }

  onSortOption(selectedSort?: SortingType) {
    if (selectedSort) this.requestData.sorting = selectedSort;
    this.reloadTable();
  }

  ensureDate(dateTime: unknown): Date | null {
    return dateTime instanceof Date ? dateTime : null;
  }

  selectedDate: string | null = '';
  onDateRangeChanged(input: DateRangeInput): void {
    const result = processDateRange(input);

    this.requestData.startDate = result.startDate;
    this.requestData.endDate = result.endDate;
    this.selectedDate = result.selectedDate;
    this.filterDatesInTable();
  }

  clearDates(): void {
    this.requestData.startDate = undefined;
    this.requestData.endDate = undefined;
    this.reloadTable();
  }

  toggleModal() {
    this.isDatepickerOpen = !this.isDatepickerOpen;
  }

  filterDatesInTable() {
    this.client.getFraudulentMeters(this.requestData).subscribe({
      next: (response) => {
        this.data = response.data;
        this.numberOfMeters = response.count || 0;
        this.lastPageSize = Math.ceil(this.numberOfMeters / this.pageSize);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  download() {
    const fileNameApendix = generateFileNameAppendix();

    const data = new DownloadFraudAlarmDataIn({
      search: this.requestData.search,
      startDate: this.requestData.startDate,
      endDate: this.requestData.endDate,
      sorting: this.requestData.sorting,
    });

    this.client.downloadFraudulentMetersFile(data).subscribe({
      next: (response) => {
        this.fileService.downloadFile(
          response.data,
          response.fileName || `${'FraudulentMeters'}${fileNameApendix}`,
        );
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }
}
